/**
 * The prototype's player: makes a prototype document (`html.ts`) clickable. One screen shows at a
 * time at the device's size, scaled to fit; a click on an element with `data-go` leads to its
 * screen (or back) with its transition; Enter in an input clicks the screen's first button that
 * leads somewhere; Back goes the way it came.
 *
 * It is one self-contained function on purpose: the Prototype window runs it from the app on a
 * frame that may run no script of its own (the app's CSP forbids inline scripts), and the
 * exported site writes its text into its `<script>` (`playPrototype.toString()`). So it uses
 * nothing from outside itself: no import, no helper, no variable of this module.
 */

/** What the player tells about the screen shown. */
export interface PlayedScreen {
  id: string;
  name: string;
  /** Whether Back leads somewhere. */
  back: boolean;
}

/** A prototype being played. */
export interface Player {
  /** Back to the screen before. */
  back(): void;
  /** Back to the first screen, forgetting the way. */
  restart(): void;
  /** Shows a screen straight away. */
  go(id: string): void;
  /** The screen shown. */
  current(): string | null;
  /** Stops listening (the frame is reloaded or closed). */
  stop(): void;
}

/** Plays the prototype in `doc` from the screen `start` (none: the first). */
export function playPrototype(doc: Document, options: { start: string | null; onScreen?: (screen: PlayedScreen) => void }): Player {
  const win = doc.defaultView!;
  const device = doc.getElementById("device") as HTMLElement | null;
  const screens = Array.from(doc.querySelectorAll<HTMLElement>("[data-screen]"));
  const byId = (id: string | null) => screens.find((s) => s.dataset.screen === id) ?? null;
  const first = byId(options.start)?.dataset.screen ?? screens[0]?.dataset.screen ?? null;
  const bar = { back: doc.getElementById("bar-back") as HTMLButtonElement | null, name: doc.getElementById("bar-name"), restart: doc.getElementById("bar-restart") };
  const way: { id: string; how: string }[] = [];
  let current: string | null = null;

  /** Sizes the device to the screen shown and scales it to fit the window. */
  const fit = () => {
    const shown = byId(current);
    if (!shown || !device) return;
    const w = Number(shown.dataset.w) || 390;
    const h = Number(shown.dataset.h) || 844;
    device.style.width = `${w}px`;
    device.style.height = `${h}px`;
    const room = bar.name ? 72 : 16;
    const k = Math.min(1, (win.innerWidth - 16) / w, (win.innerHeight - room) / h);
    device.style.transform = k < 1 ? `scale(${k})` : "";
  };

  /** Shows screen `id`, sliding in from the side it comes from. */
  const show = (id: string, how: string, backwards: boolean) => {
    const next = byId(id);
    if (!next) return;
    const was = byId(current);
    for (const s of screens) if (s !== next && s !== was) s.hidden = true;
    next.hidden = false;
    next.scrollTop = 0;
    next.style.zIndex = "2";
    if (was && was !== next) was.style.zIndex = "1";
    current = id;
    fit();
    const animated = was && was !== next && how !== "instant" && typeof next.animate === "function";
    if (animated) {
      const sign = backwards ? -1 : 1;
      const run =
        how === "slide"
          ? (was.animate([{ transform: "none" }, { transform: `translateX(${-30 * sign}%)` }], { duration: 280, easing: "ease-out" }), next.animate([{ transform: `translateX(${100 * sign}%)` }, { transform: "none" }], { duration: 280, easing: "ease-out" }))
          : next.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220 });
      run.onfinish = () => {
        if (current !== id) return;
        was.hidden = true;
      };
    } else if (was && was !== next) was.hidden = true;
    const name = next.dataset.name ?? "";
    if (bar.name) bar.name.textContent = name;
    if (bar.back) bar.back.disabled = way.length === 0;
    options.onScreen?.({ id, name, back: way.length > 0 });
  };

  /** Follows an element's way: to its screen, or back. */
  const follow = (el: HTMLElement) => {
    const go = el.dataset.go;
    const how = el.dataset.transition ?? "instant";
    if (!go) return;
    if (go === "back") return player.back();
    if (go === current || !byId(go)) return;
    if (current) way.push({ id: current, how });
    show(go, how, false);
  };

  const onClick = (e: MouseEvent) => {
    const el = (e.target as Element | null)?.closest<HTMLElement>("[data-go]");
    if (!el || el.tagName === "INPUT") return;
    follow(el);
  };
  const onKey = (e: KeyboardEvent) => {
    const input = e.target as HTMLElement | null;
    if (e.key !== "Enter" || input?.tagName !== "INPUT" || (input as HTMLInputElement).type === "checkbox") return;
    const target = input.dataset.go ? input : input.closest("[data-screen]")?.querySelector<HTMLElement>("button[data-go], .button[data-go]");
    if (target) {
      e.preventDefault();
      follow(target);
    }
  };
  const onBack = () => player.back();
  const onRestart = () => player.restart();

  const player: Player = {
    back() {
      const before = way.pop();
      if (before) show(before.id, before.how, true);
    },
    restart() {
      way.length = 0;
      if (first) show(first, "instant", false);
      for (const input of Array.from(doc.querySelectorAll<HTMLInputElement>("input"))) {
        if (input.type === "checkbox") input.checked = input.defaultChecked;
        else input.value = "";
      }
    },
    go(id) {
      way.length = 0;
      show(id, "instant", false);
    },
    current: () => current,
    stop() {
      doc.removeEventListener("click", onClick);
      doc.removeEventListener("keydown", onKey);
      win.removeEventListener("resize", fit);
      bar.back?.removeEventListener("click", onBack);
      bar.restart?.removeEventListener("click", onRestart);
    },
  };
  doc.addEventListener("click", onClick);
  doc.addEventListener("keydown", onKey);
  win.addEventListener("resize", fit);
  bar.back?.addEventListener("click", onBack);
  bar.restart?.addEventListener("click", onRestart);
  if (first) show(first, "instant", false);
  return player;
}
